import React from 'react';
import { Loader2 } from 'lucide-react';

// --- BUTTONS ---

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  isLoading, 
  className = '', 
  disabled,
  ...props 
}) => {
  const baseStyle = "rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const sizes = {
    sm: "px-3 py-1 text-sm",
    md: "px-4 py-2",
    lg: "px-6 py-3 text-lg"
  };

  const variants = {
    primary: "bg-brand-primary text-white hover:bg-red-600 shadow-md hover:shadow-lg",
    secondary: "bg-brand-secondary text-brand-text hover:bg-yellow-300 shadow-sm",
    outline: "border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white",
    ghost: "hover:bg-brand-text/5 text-brand-text"
  };

  return (
    <button 
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${className}`} 
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

// --- INPUTS ---

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export const Input: React.FC<InputProps> = ({ label, className = '', ...props }) => {
  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <label className="text-xs font-bold uppercase tracking-wider text-brand-text/70">{label}</label>}
      <input 
        className={`bg-white border-2 border-transparent focus:border-brand-primary rounded-lg px-4 py-2 outline-none transition-all shadow-sm text-brand-text ${className}`}
        {...props}
      />
    </div>
  );
};

// --- CARDS ---

// Fix: Cast children to any to be permissive with what's passed in
export const Card: React.FC<{ children: any; className?: string; onClick?: () => void }> = ({ 
  children, 
  className = '',
  onClick
}) => {
  return (
    <div 
      onClick={onClick}
      className={`bg-white rounded-3xl p-6 shadow-xl shadow-brand-text/5 border border-brand-text/5 hover:shadow-2xl transition-all duration-300 ${className}`}
    >
      {children}
    </div>
  );
};

// --- BADGES ---

export const Badge: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = 'bg-brand-accent' }) => (
  <span className={`${color} text-brand-text text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-widest`}>
    {children}
  </span>
);